declare global {
    interface HTMLElement {
        /**
         * Define o valor de um elemento de formulário (input, select ou textarea).
         * @param value - Valor a ser atribuído ao elemento.
         */
        setValue(value: string): void
        /**
         * Retorna o valor atual de um elemento de formulário (input, select ou textarea) ou o texto de um span.
         * @returns O valor atual do elemento, ou uma string vazia se o elemento não for suportado.
         */
        getValue(): string
        /**
         * Limpa o valor de um elemento de formulário (input, select ou textarea).
         */
        clear(): void
    }
}

HTMLElement.prototype.setValue = function (value: string = ''): void {
    if (
        this instanceof HTMLInputElement ||
        this instanceof HTMLSelectElement ||
        this instanceof HTMLTextAreaElement
    ) {
        this.value = String(value)
    }
}
HTMLElement.prototype.getValue = function (): string {
    if (this instanceof HTMLSpanElement) {
        return this.textContent || ''
    }
    if (
        this instanceof HTMLInputElement ||
        this instanceof HTMLSelectElement ||
        this instanceof HTMLTextAreaElement
    ) {
        return this.value
    }
    return ''
}
HTMLElement.prototype.clear = function (): void {
    if (
        this instanceof HTMLInputElement ||
        this instanceof HTMLSelectElement ||
        this instanceof HTMLTextAreaElement
    ) {
        this.value = ''
    }
}

/**
 * Abstração para document.querySelector, com suporte opcional para um elemento pai específico.
 * @param selector - Uma string com um [seletor CSS](https://developer.mozilla.org/pt-BR/docs/Web/CSS/CSS_selectors).
 * @param parent - Elemento HTML opcional como ponto de partida para a busca. Se omitido, a busca ocorre em todo o documento.
 * @returns O primeiro elemento HTML que corresponde ao seletor, ou `null` se não houver correspondência.
 */
export function get(selector: string, parent?: HTMLElement): HTMLElement | null {
    const root = parent ?? document
    return root.querySelector<HTMLElement>(selector)
}

/**
 * Abstração para document.querySelectorAll, com suporte opcional para um elemento pai específico.
 * @param selector - Uma string com um [seletor CSS](https://developer.mozilla.org/pt-BR/docs/Web/CSS/CSS_selectors).
 * @param parent - Elemento HTML opcional como ponto de partida para a busca. Se omitido, a busca ocorre em todo o documento.
 * @returns Uma matriz de elementos HTML que correspondem ao seletor.
 */
export function getAll(selector: string, parent?: HTMLElement): HTMLElement[] {
    const root = parent ?? document
    return Array.from(root.querySelectorAll<HTMLElement>(selector))
}

/**
 * Retorna os dados de um formulário como um objeto JSON.
 * @param formId - ID do formulário HTML.
 * @returns Um objeto JSON contendo os dados do formulário.
 * @throws {Error} Se `formId` não for fornecido.
 */
export function getData(formId: string) {
    if (!formId) {
        throw new Error('O ID do formulário é obrigatório.')
    }
    const form = document.getElementById(formId) as HTMLFormElement
    const formData = new FormData(form)
    const jsonData: { [key: string]: FormDataEntryValue } = {}
    formData.forEach((value, key) => {
        jsonData[key] = value
    })
    return jsonData
}

